'use client';

import React, { useState, useRef, useEffect } from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { classNames } from '@repo/ui/src/utils/classNames';

interface ExpandableTextProps {
  text: string;
  collapsedLines?: number;
  showMoreText?: string;
  showLessText?: string;
  className?: string;
}

const ExpandableText = ({
  text,
  collapsedLines = 3,
  showMoreText,
  showLessText,
  className,
}: ExpandableTextProps) => {
  const [expanded, setExpanded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [expandedHeight, setExpandedHeight] = useState(0);
  const [collapsedHeight, setCollapsedHeight] = useState(0);
  const [isExpandable, setIsExpandable] = useState(false);

  const { t } = useTranslation();
  const contentRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkHeight = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      const collapsed = lineHeight * collapsedLines;
      setExpandedHeight(el.scrollHeight);
      setCollapsedHeight(collapsed);
      setIsExpandable(el.scrollHeight > collapsed);
    };

    checkHeight();

    const resizeObserver = new ResizeObserver(() => checkHeight());
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [text, collapsedLines]);

  const toggle = () => {
    setExpanded(!expanded);
    setIsAnimating(true);
  };

  const resolvedShowMore =
    showMoreText ?? t('expandableText.showMore', undefined, { default: 'Show more' });
  const resolvedShowLess =
    showLessText ?? t('expandableText.showLess', undefined, { default: 'Show less' });

  return (
    <div className={classNames('overflow-hidden relative', className)}>
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: expanded ? `${expandedHeight}px` : `${collapsedHeight}px`,
        }}
        onTransitionEnd={() => setIsAnimating(false)}
      >
        <p
          ref={contentRef}
          className="mb-0 leading-6 text-ellipsis transition-all duration-300"
          style={
            isExpandable && !expanded && !isAnimating
              ? {
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: collapsedLines,
                }
              : undefined
          }
        >
          {text}
        </p>
      </div>
      {isExpandable && (
        <button
          className="font-bold underline bg-transparent border-0 p-0 cursor-pointer"
          onClick={toggle}
        >
          {expanded ? resolvedShowLess : resolvedShowMore}
        </button>
      )}
    </div>
  );
};

export default ExpandableText;
